// pages/ToshbaMishnaFoundations.js
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';
import { SkeletonCardGrid } from '../components/ui';
import { useResourceDetailModal } from '../components/ResourceDetailModalContext';

export default function ToshbaMishnaFoundations() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        // Filter files by subtopic_mishna_type='FOUNDATIONS'
        const typeValue = encodeURIComponent('FOUNDATIONS');
        const res = await fetchWithAuth(`/api/subtopic-mishna-files/?subtopic_mishna_type=${typeValue}`);
        if (res.ok) {
          const data = await res.json();
          if (!ignore) setFiles(data);
        } else {
          throw new Error('Failed to fetch files');
        }
      } catch {
        if (!ignore) setError('שגיאה בטעינת התוכן');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="h-6 w-64 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mb-6" />
        <div className="h-8 w-32 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mx-auto mb-8" />
        <SkeletonCardGrid count={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <Breadcrumbs
        items={[
          { to: '/disciplines', label: 'תחומי דעת' },
          { to: '/toshba/classes', label: 'תושב"ע' },
          { to: '/toshba/classes/mishna', label: 'משנה' },
          { label: 'המלצות להוראה מיטבית' },
        ]}
      />

      <div className="toolbar-center">
        <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 dark:text-white">
          המלצות להוראה מיטבית
        </h2>
      </div>

      <p className="text-center mt-2 mb-6">
        <Link
          to="/toshba/classes/mishna"
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          חזרה למשנה
        </Link>
      </p>

      {files.length > 0 ? (
        <div className="grid-autofit">
          {files.map(file => (
            <ResourceCard key={file.id} item={file} />
          ))}
        </div>
      ) : (
        <p className="text-center text-secondary-500 dark:text-secondary-400 py-8">
          אין קבצים להצגה.
        </p>
      )}
    </div>
  );
}

function ResourceCard({ item }) {
  const { openResourceDetail } = useResourceDetailModal();
  const hasFile = !!item.file;
  const isExternal = !!item.url && !hasFile;
  const downloadUrl = hasFile
    ? `/api/subtopic-mishna-files/${item.id}/download/`
    : item.url || item.file;

  const handleOpen = () => {
    if (!item.use_detail_modal && downloadUrl) {
      window.open(downloadUrl, isExternal ? '_blank' : '_self', isExternal ? 'noopener,noreferrer' : undefined);
      return;
    }

    const modalItem = {
      id: item.id,
      model_type: 'toshba_mishna_file_foundations',
      title: item.title || 'קובץ ללא כותרת',
      summary: item.explanation || '',
      explanation: item.explanation || '',
      author: item.author || '',
      subjectLabel: 'תושב״ע - משנה',
      typeLabel: 'קובץ',
      grades: item.grades || [],
      tags: [],
      downloadUrl,
      date: item.created_at || '',
    };
    openResourceDetail(modalItem);
  };

  return (
    <article className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
      <button
        type="button"
        onClick={handleOpen}
        className="block w-full relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
        style={{ aspectRatio: '3/2' }}
      >
        {item.image ? (
          <img src={item.image} alt={item.title || 'קובץ'} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        )}
      </button>
      <div className="p-4 md:p-5">
        <h4 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
          <button
            type="button"
            onClick={handleOpen}
            className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-right w-full text-inherit"
          >
            {item.title || 'קובץ ללא כותרת'}
          </button>
        </h4>
        {item.author && <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">{item.author}</p>}
      </div>
    </article>
  );
}

